import React, { cloneElement, ReactElement, ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import styled from 'styled-components';
import { addNotification } from '../../../store';

//region INTERFACES
interface FileDropContainerProps {
  children: ReactNode;
  onUpload: (data: FileList) => void;
  renderAsItem?: boolean;
  isDisabled?: boolean;
  isItemContainer?: boolean;
  minHeight?: string;
  acceptedMimeTypes?: string[] | null;
}

//endregion

//region STYLED
const StyledDropContainer = styled.div<{$minHeight?: string}>`
  outline: 3px dashed transparent;
  outline-offset: 4px;
  height: 100%;
  width: 100%;
  min-width: 0;

  &.bg-container {
    min-height: ${({ $minHeight }) => $minHeight || '60vh'};
  }

  .drop-over {
    outline: 3px dashed blue;
    outline-offset: 4px;
    transition: outline-color 0s ease-in-out;
  }

  &.drop-over {
    background-color: transparent;
    outline: 3px dashed blue;
    outline-offset: 4px;
    transition: outline-color 0s ease-in-out;
  }
`;
//endregion

export const FileDropContainer = ({
  children,
  onUpload,
  minHeight,
  isItemContainer = false,
  isDisabled = false,
  renderAsItem = false,
  acceptedMimeTypes,
}: FileDropContainerProps) => {
  const dispatch = useDispatch();
  const [isOverContainer, setIsOverContainer] = useState(false);

  const handleContainerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFiles = event.dataTransfer.files;

    if (typeof onUpload !== 'function') {
      console.error('Error: invalid prop type for onUpload');
      return;
    }

    if (droppedFiles && droppedFiles.length > 0) {
      const hasUnsupportedFile =
        !!acceptedMimeTypes && Array.from(droppedFiles).some((file) => !acceptedMimeTypes.includes(file.type));

      if (hasUnsupportedFile) {
        dispatch(addNotification({ type: 'warning', message: 'Unsupported file type.' }));
        setIsOverContainer(false);
        return;
      }

      onUpload(droppedFiles);
    }

    setIsOverContainer(false);
  };

  const handleDragOverContainer = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (isItemContainer) {
      event.stopPropagation();
    }

    setIsOverContainer(true);
  };

  const handleDragLeaveContainer = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    setIsOverContainer(false);
    //@todo - fix this
    // // Ensure the drag leaves the container, not just moves between elements
    // if (!event.currentTarget.contains(event.relatedTarget as Node)) {
    //   setIsOverContainer(false)
    // }
  };

  const childrenWithDropOverClass = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return cloneElement(child as ReactElement<any>, {
        className: classNames((child.props as any).className, {
          'drop-over': isOverContainer,
          'item-over': isItemContainer,
        }),
      });
    }
    return child;
  });

  if (isDisabled) {
    return <>{children}</>;
  }

  return renderAsItem ? (
    <>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? cloneElement(child as ReactElement<any>, {
              onDragOver: handleDragOverContainer,
              onDrop: handleContainerDrop,
              onDragLeave: handleDragLeaveContainer,
              className: classNames((child.props as any).className, { 'drop-over': isOverContainer }),
            })
          : child,
      )}
    </>
  ) : (
    <StyledDropContainer
      $minHeight={minHeight}
      className={classNames({ 'bg-container': !isItemContainer, 'drop-over': isOverContainer })}
      onDragOver={handleDragOverContainer}
      onDrop={handleContainerDrop}
      onDragLeave={handleDragLeaveContainer}
    >
      {childrenWithDropOverClass}
    </StyledDropContainer>
  );
};
